import useSWR from 'swr';
import { sdk } from '@/sdk';
import { Product as SharedProduct } from '@shared/types/product';
import { SupersededBy } from '@/types/entity/product';

// EWM-194 — resolve CT product references (raw `productReplacements[].id`)
// into the rich `SupersededBy[]` list the Superseded alert + discontinued
// state need.
//
// CT stores the replacement attribute as
// `[{ typeId: 'product', id: '<uuid>' }, ...]` per variant (verified against
// SB2486 → SB2487 on electus-dev) and can hold multiple entries. The BFF
// `product/query` action accepts `productIds`, so no BE-side reference
// expansion is required — a single client-side query resolves all IDs in
// one call.
//
// Returns `undefined` while loading, when no IDs are provided, or when no
// referenced product resolves to a complete `{ name, sku, url }`. The
// alert / discontinued state hides the replacement section in those cases.
const useSupersededBy = (refIds: string[] | undefined): SupersededBy[] | undefined => {
  const ids = refIds && refIds.length > 0 ? refIds : undefined;
  const { data } = useSWR(ids ? ['/action/product/query', 'supersededBy', ...ids] : null, () =>
    sdk.composableCommerce.product.query({ productIds: ids as string[] }),
  );

  if (!ids || data?.isError) return undefined;

  const products = (data?.data?.items as SharedProduct[] | undefined) ?? [];
  const resolved: SupersededBy[] = [];
  for (const product of products) {
    const masterSku = product?.variants?.[0]?.sku;
    if (!product?.name || !masterSku) continue;
    // PDP URL pattern is `/{slug}/p/{sku}` (see backend ProductRouter).
    // Prefer the BFF-mapped `_url` when present so route resolution stays in
    // sync with whatever the BFF emits today; fall back to the slug-based
    // template when it isn't.
    const url = product._url ?? (product.slug ? `/${product.slug}/p/${masterSku}` : undefined);
    if (!url) continue;
    resolved.push({ name: product.name, sku: masterSku, url });
  }
  return resolved.length > 0 ? resolved : undefined;
};

export default useSupersededBy;
